import { FilterModel } from '@hooks/useFiltersSearchParams';
import { getParams, sellerApiClient } from './sellerApiClient';
import { PaginationModel } from '@hooks/usePagingSearchParams';
import { Seller } from './index';
import { useQuery } from 'react-query';

const defaultParams: PaginationModel = { page: 1, pageSize: 1 };

export type TRequestParams = {
  filters?: FilterModel[];
  page: PaginationModel;
};

export type TNewParcelItem = {
  imageUrl: string;
  name: string;
  quantity: number;
  url: string;
  vendorCode: string;
};

export type TNewParcel = {
  createdAt: Date;
  externalId: string;
  id: string;
  items: TNewParcelItem[];
  seller: Seller;
};

export type TNewParcelsResponse = {
  total: number;
  items: TNewParcel[];
};

export const getNew = async (params: TRequestParams): Promise<TNewParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TNewParcelsResponse>('/v1/dropshipping/new-parcels/ozon/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useNewCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['new', defaultParams],
    queryFn: () => getNew({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TNewParcelsCount = {
  total: number;
};

export const getOzon = async (): Promise<TNewParcelsCount> => {
  const response = await sellerApiClient.get<TNewParcelsCount>('v1/dropshipping/new-parcels/ozon/page/count');
  return response.data;
};

export const useOzonCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['ozon', defaultParams],
    queryFn: () => getOzon(),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TAddMatchingParams = {
  parcelExternalIds: string[];
  message: string | null;
  externalMarketplaceCodes: string[];
};

export const addMatching = async (params: TAddMatchingParams): Promise<{ matchingOrderId: string}> => {
  const response = await sellerApiClient.post<{ matchingOrderId: string}>('/v1/dropshipping/matching-order/create', params);
  return response.data;
};

export type TInProcessParcelItemAttachment = {
  image: string;
  name: string;
  url: string;
  vendorCode: string;
};

export type TInProcessParcelItem = {
  externalId: string;
  items: TInProcessParcelItemAttachment[];
  seller?: Seller;
};

export type TInProcessParcelExternal = {
  externalMarketplaceTrackNumber: string;
  id: string;
  inDropOfPointAt: Date;
  isInDropOfPoint: boolean;
};

export type TInProcessParcel = {
  createdAt: Date;
  externalParcels: TInProcessParcelExternal[];
  externalMatchingId?: string;
  id: string;
  message: string;
  parcels: TInProcessParcelItem[];
  status: string;
  trackNumber: string;
};

export type TInProcessParcelsResponse = {
  total: number;
  items: TInProcessParcel[];
};

export const getInProcess = async (params: TRequestParams): Promise<TInProcessParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TInProcessParcelsResponse>('/v1/dropshipping/matching/page', {
    params: getParams(page, [{ name: 'Statuses', value: ['Created', 'Confirmed'] }, ...(filters ?? [])]),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useInProcessCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['inProcess', defaultParams],
    queryFn: () => getInProcess({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TEditMatchingExternalParcel = {
  externalMarketplaceTrackNumber: string;
  id?: string | null;
};

export type TEditMatchingParams = {
  externalParcels: TEditMatchingExternalParcel[];
  matchingId: string;
  message: string | null;
};

export const editMatching = async (params: TEditMatchingParams): Promise<void> => {
  const response = await sellerApiClient.put<void>(`/v1/dropshipping/matching/${params.matchingId}`, params);
  return response.data;
};

export const removeMatching = async (id: string): Promise<void> => {
  const response = await sellerApiClient.delete<void>(`/v1/dropshipping/matching/${id}`);
  return response.data;
};

export type TCompleteParcelItemAttachment = {
  image: string;
  name: string;
  url: string;
  vendorCode: string;
};

export type TCompleteParcelItem = {
  externalId: string;
  items: TCompleteParcelItemAttachment[];
  seller: Seller;
};

export type TCompleteParcelExternal = {
  externalMarketplaceTrackNumber: string;
  id: string;
  inDropOfPointAt: Date;
  isInDropOfPoint: boolean;
};

export type TCompleteParcel = {
  createdAt: Date;
  externalParcels: TCompleteParcelExternal[];
  id: string;
  message: string;
  parcels: TCompleteParcelItem[];
  status: string;
  trackNumber: string;
};

export type TCompleteParcelsResponse = {
  total: number;
  items: TCompleteParcel[];
};

export const getComplete = async (params: TRequestParams): Promise<TCompleteParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TCompleteParcelsResponse>('/v1/dropshipping/matching/page', {
    params: getParams(page, [{ name: 'Statuses', value: ['Completed'] }, ...(filters ?? [])]),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const useCompleteCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['complete', defaultParams],
    queryFn: () => getComplete({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export interface TMatchingTariff {
  id: string;
  countryCode: string;
  price: {
    amount: number;
    currency: string;
  };
}

export interface TGetMatchingTariffsResponse {
  tariffs: TMatchingTariff[];
}

export const getMatchingTariffs = async (): Promise<TGetMatchingTariffsResponse> => {
  const response = await sellerApiClient.get<TGetMatchingTariffsResponse>(
    '/v1/dropshipping/matching-order/tariffs',
  );
  return response.data;
};

export interface TGetMatchingOrderStatusResponse {
  status: string;
}

export const getMatchingStatus = async (id: string): Promise<TGetMatchingOrderStatusResponse> => {
  const response = await sellerApiClient.get<TGetMatchingOrderStatusResponse>(
    `/v1/dropshipping/matching-order/${id}/status`,
  );
  return response.data;
};
